// Client portal Self Ship tab — read-only fulfillments list. The backend
// censor strips carrier_cost out before it reaches the browser, so the
// table here only renders client-facing fee columns.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, AlertTriangle } from 'lucide-react'
import { getMeFulfillments } from '../../../lib/api'
import {
  formatCurrency, formatInt, formatDate,
  currentMonthStart, todayStr, DateRangeFilter,
} from './helpers'

const FEE_COLS = [
  { key: 'pick_fee',       label: 'Pick' },
  { key: 'pack_fee',       label: '+Items' },
  { key: 'handling_fee',   label: 'Label' },
  { key: 'insert_fee',     label: 'Insert' },
  { key: 'shipping_cost',  label: 'Shipping' },
]

export default function SelfShipTab({ clientId }) {
  const [from, setFrom] = useState(currentMonthStart())
  const [to,   setTo]   = useState(todayStr())

  const params = useMemo(() => ({
    ...(from && { from }),
    ...(to   && { to   }),
  }), [from, to])

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['me', 'fulfillments', clientId, params],
    queryFn:  () => getMeFulfillments(clientId, params),
    enabled:  !!clientId,
  })

  const totals = useMemo(() => {
    const out = { count: rows.length }
    for (const k of FEE_COLS.map(c => c.key)) {
      out[k] = rows.reduce((s, r) => s + (Number(r[k]) || 0), 0)
    }
    out.total = FEE_COLS.reduce((s, c) => s + out[c.key], 0)
    return out
  }, [rows])

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Self-shipped orders</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Orders fulfilled where you arranged the carrier yourself.
              Fee columns are what we bill you for handling them.
            </p>
          </div>
          <DateRangeFilter from={from} to={to} onChange={({ from: f, to: t }) => { setFrom(f); setTo(t) }} />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : error ? (
          // Error before empty: with `rows` defaulted to [] a failed fetch would
          // read as "no self-shipped orders", i.e. no fees for the period.
          <div className="p-6 text-center">
            <AlertTriangle size={22} className="mx-auto text-red-500 mb-2" />
            <p className="text-sm font-medium text-red-700">Couldn't load self-shipped orders</p>
            <p className="text-xs text-red-600 mt-1">
              This is a load failure, not an empty period — orders and fees may exist
              that aren't shown. Try again before relying on this list.
            </p>
            <p className="text-xs text-slate-400 mt-1.5 font-mono">
              {error?.response?.data?.detail || error?.message || 'Unknown error'}
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No self-shipped orders in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2.5">Ship Date</th>
                  <th className="px-3 py-2.5">Order #</th>
                  <th className="px-3 py-2.5">Recipient</th>
                  <th className="px-3 py-2.5">State</th>
                  <th className="px-3 py-2.5">Carrier</th>
                  <th className="px-3 py-2.5">Tracking</th>
                  <th className="px-3 py-2.5 text-right">Items</th>
                  {FEE_COLS.map(c => (
                    <th key={c.key} className="px-3 py-2.5 text-right">{c.label}</th>
                  ))}
                  <th className="px-3 py-2.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => {
                  const total = FEE_COLS.reduce((s, c) => s + (Number(r[c.key]) || 0), 0)
                  return (
                    <tr key={r.id || i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                      <td className="px-3 py-2 text-slate-600">{formatDate(r.ship_date)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{r.order_number || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.recipient_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.recipient_state || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{r.carrier_code || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.tracking_number || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatInt(r.item_count)}</td>
                      {FEE_COLS.map(c => (
                        <td key={c.key} className="px-3 py-2 text-right tabular-nums text-slate-700">
                          {formatCurrency(r[c.key])}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">
                        {formatCurrency(total)}
                      </td>
                    </tr>
                  )
                })}
                <tr className="bg-slate-50 border-t-2 border-slate-200">
                  <td colSpan={6} className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total ({totals.count} order{totals.count !== 1 ? 's' : ''})
                  </td>
                  <td />
                  {FEE_COLS.map(c => (
                    <td key={c.key} className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-700">
                      {formatCurrency(totals[c.key])}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-800">{formatCurrency(totals.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
